import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Content, Header, InfoCard, Link, Page, Progress, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FreezeSection } from './FreezeSection';

export type NewsEntry = {
  id: string;
  kind: 'novedad' | 'aviso' | 'freeze';
  title: string;
  description: string;
  link?: string;
  audience: string;
  starts_at?: string;
  ends_at?: string;
  created_by: string;
  created_at: string;
};

const DEVOPS = 'group:default/devops';
const KIND = { novedad: 'Novedad', aviso: 'Aviso', freeze: 'Freeze' };
// El freeze se lanza desde su sección (workflow real), no como entrada a mano.
const FORM_KINDS = ['novedad', 'aviso'] as const;
const COLOR = { novedad: 'primary', aviso: 'default', freeze: 'secondary' } as const;
const EMPTY = { kind: 'novedad', title: '', description: '', link: '', audience: 'all', starts_at: '', ends_at: '', notify: true };

const fmt = (d?: string) => (d ? new Date(d).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const active = (e: NewsEntry) =>
  (!e.starts_at || new Date(e.starts_at) <= new Date()) &&
  (e.ends_at ? new Date(e.ends_at) > new Date() : e.kind === 'freeze');

/** CHANGELOG de DevOps: lo entregado y los avisos/freeze. El grupo devops publica y avisa a todos o a un grupo. */
export const DevopsNewsPage = () => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const identity = useApi(identityApiRef);
  const catalog = useApi(catalogApiRef);
  const [entries, setEntries] = useState<NewsEntry[]>();
  const [error, setError] = useState<string>();
  const [isDevops, setIsDevops] = useState(false);
  const [groups, setGroups] = useState<string[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${await discovery.getBaseUrl('devops-news')}/entries`);
    if (!res.ok) throw new Error(`El backend respondió ${res.status}`);
    setEntries(await res.json());
  }, [discovery, fetch]);

  useEffect(() => {
    load().catch(e => setError(String(e?.message ?? e)));
    identity.getBackstageIdentity().then(i => setIsDevops(i.ownershipEntityRefs.includes(DEVOPS)));
  }, [load, identity]);

  useEffect(() => {
    if (!isDevops) return;
    catalog
      .getEntities({ filter: { kind: 'Group' }, fields: ['metadata.name', 'metadata.namespace'] })
      .then(r => setGroups(r.items.map(g => `group:${g.metadata.namespace ?? 'default'}/${g.metadata.name}`).sort()));
  }, [isDevops, catalog]);

  const set = (key: keyof typeof EMPTY) => (e: any) =>
    setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const publish = async () => {
    setSending(true);
    setError(undefined);
    try {
      const body = {
        ...form,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : undefined,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
      };
      const res = await fetch(`${await discovery.getBaseUrl('devops-news')}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
      setForm(EMPTY);
      await load();
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setSending(false);
    }
  };

  const end = async (id: string) => {
    await fetch(`${await discovery.getBaseUrl('devops-news')}/entries/${id}/end`, { method: 'POST' });
    await load();
  };

  return (
    <Page themeId="tool">
      <Header title="Novedades DevOps" subtitle="Lo que DevOps entrega, avisos y freeze" />
      <Content>
        {error && <WarningPanel title="Algo falló" message={error} />}
        <FreezeSection onChange={() => load().catch(() => undefined)} />
        <div style={{ height: 16 }} />
        {isDevops && (
          <InfoCard title="Publicar (solo devops)">
            <Grid container spacing={2}>
              <Grid item md={3} xs={12}>
                <TextField select fullWidth label="Tipo" value={form.kind} onChange={set('kind')}>
                  {FORM_KINDS.map(k => (
                    <MenuItem key={k} value={k}>{KIND[k]}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item md={9} xs={12}>
                <TextField fullWidth label="Título" value={form.title} onChange={set('title')} inputProps={{ maxLength: 200 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label="Descripción" value={form.description} onChange={set('description')} />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField fullWidth label="Enlace (opcional: /ruta o https://…)" value={form.link} onChange={set('link')} />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField select fullWidth label="Para" value={form.audience} onChange={set('audience')}>
                  <MenuItem value="all">Todos</MenuItem>
                  {groups.map(g => (
                    <MenuItem key={g} value={g}>{g.replace(/^group:default\//, '')}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {form.kind !== 'novedad' && (
                <>
                  <Grid item md={6} xs={12}>
                    <TextField fullWidth type="datetime-local" label="Desde (vacío = ahora)" InputLabelProps={{ shrink: true }} value={form.starts_at} onChange={set('starts_at')} />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <TextField fullWidth type="datetime-local" label="Hasta (obligatorio para que salga la franja)" InputLabelProps={{ shrink: true }} value={form.ends_at} onChange={set('ends_at')} />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <FormControlLabel control={<Checkbox checked={form.notify} onChange={set('notify')} color="primary" />} label="Avisar por notificación" />
                <Button variant="contained" color="primary" disabled={sending || !form.title || !form.description} onClick={publish}>
                  Publicar
                </Button>
              </Grid>
            </Grid>
          </InfoCard>
        )}
        {!entries && !error && <Progress />}
        {entries?.map(e => (
          <Paper key={e.id} style={{ padding: 16, marginTop: 16, borderLeft: active(e) ? '4px solid #f44336' : undefined }}>
            <Typography variant="caption" color="textSecondary">
              {fmt(e.created_at)} · {e.created_by.replace(/^user:default\//, '')}
              {e.audience !== 'all' && ` · para ${e.audience.replace(/^group:default\//, '')}`}
            </Typography>
            <Typography variant="h6">
              <Chip size="small" label={KIND[e.kind]} color={COLOR[e.kind]} style={{ marginRight: 8 }} />
              {e.link ? <Link to={e.link}>{e.title}</Link> : e.title}
            </Typography>
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>{e.description}</Typography>
            {(e.ends_at || e.kind === 'freeze') && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                {active(e) ? 'Vigente' : 'Terminado'}: {e.starts_at ? `${fmt(e.starts_at)} → ` : 'hasta '}{e.ends_at ? fmt(e.ends_at) : 'hasta que devops lo termine'}
                {isDevops && active(e) && e.kind !== 'freeze' && (
                  <Button size="small" style={{ marginLeft: 8 }} onClick={() => end(e.id)}>Terminar ahora</Button>
                )}
              </Typography>
            )}
          </Paper>
        ))}
      </Content>
    </Page>
  );
};
